import { ERP_BOOLEAN } from '@/constants/keys';
import { ATTRIBUTES } from '@/constants/product';
import { PRICE_VALIDITY_DAYS, RETURN_POLICY, SHIPPING_POLICY, STRUCTURED_DATA_ID } from '@/constants/structuredData';
import { getOrganizationId } from '@/linkedData/index-lkd';
import { availableInDays } from '@/utils/availabilityIndicatorUtils';
import { getAttributeId, getAttributeShortValue, getAttributeValue, isPrimeurCode } from '@/utils/productUtils';

export const SCHEMA = 'https://schema.org';

export const AVAILABILITY_URL = {
	IN_STOCK: `${SCHEMA}/InStock`,
	OUT_OF_STOCK: `${SCHEMA}/OutOfStock`,
	PRE_ORDER: `${SCHEMA}/PreOrder`,
	BACK_ORDER: `${SCHEMA}/BackOrder`,
};

const GTIN13_PATTERN = /^\d{13}$/;

/**
 * Date until which the announced prices are declared valid.
 * @param {Date} from The reference date
 * @return {string} The date, as YYYY-MM-DD
 */
export const getPriceValidUntil = (from = new Date()) =>
	new Date(from.getTime() + PRICE_VALIDITY_DAYS * 24 * 60 * 60 * 1000).toISOString().slice(0, 10);

/**
 * Container format of an item, as displayed on the product page.
 * Every variant of a group must carry it, since `size` is the dimension declared in `variesBy`.
 * @param {object} item The product item
 * @return {string|undefined} The format
 */
export const getVariantSize = (item) => {
	const capacity =
		getAttributeShortValue(item, ATTRIBUTES.CONDITIONING) ||
		getAttributeShortValue(item, ATTRIBUTES.FORMAT) ||
		getAttributeValue(item, ATTRIBUTES.CONDITIONING) ||
		item?.shortdesc;

	if (!capacity) {
		return undefined;
	}

	const nbUnit = parseInt(getAttributeValue(item, 'nbunit'), 10);
	return nbUnit > 1 ? `${nbUnit} x ${capacity}` : capacity;
};

/**
 * Price actually charged for an item: the offer price when there is one, the list price otherwise.
 * @param {object} item The product item
 * @return {number} The price
 */
export const getVariantPrice = ({ listPrice, offerPrice } = {}) => (offerPrice > 0 ? offerPrice : listPrice);

/**
 * Availability of an item, mirroring what the availability indicator displays.
 * @param {object} product The product
 * @param {object} item The product item
 * @return {string} The schema.org availability
 */
export const getVariantAvailability = (product, item) => {
	if (!(item?.stock > 0)) {
		return AVAILABILITY_URL.OUT_OF_STOCK;
	}
	// Primeur and pre-arrival wines are sold before they are deliverable
	if (isPrimeurCode(getAttributeId(product, ATTRIBUTES.IN_STOCK))) {
		return AVAILABILITY_URL.PRE_ORDER;
	}
	if (getAttributeId(item, ATTRIBUTES.PRE_ARRIVAL) === ERP_BOOLEAN.TRUE) {
		return AVAILABILITY_URL.PRE_ORDER;
	}

	return availableInDays(new Date(item.availability)) > 1 ? AVAILABILITY_URL.BACK_ORDER : AVAILABILITY_URL.IN_STOCK;
};

// The feed distinguishes the bottle GTIN from the case one. We publish the bottle GTIN,
// which is the one used to reconcile with the Shopping Graph.
export const getGtin13 = (item) => (GTIN13_PATTERN.test(item?.gtin_unit) ? item.gtin_unit : undefined);

/**
 * Shared context of every offer of a page.
 * @param {object} storeConf The store configuration
 * @return {object} The context
 */
export const buildOfferContext = (storeConf) => ({
	host: storeConf?.url ?? '',
	currency: storeConf?.currencyISO,
	priceValidUntil: getPriceValidUntil(),
	sellerId: getOrganizationId(storeConf),
});

/**
 * Build the offer of one item, with the merchant listing properties Google requires.
 * The shipping and return policies are referenced by @id, so they are defined once per page.
 * @param {object} product The product
 * @param {object} item The item actually offered
 * @param {object} context The offer context
 * @param {string} [url] The url of the offer, when it differs from the page
 * @return {object|undefined} The Offer, or undefined when the item has no price
 */
export const buildOffer = (product, item, context, url = undefined) => {
	const price = getVariantPrice(item);

	if (!(price > 0)) {
		return undefined;
	}

	return {
		'@type': 'Offer',
		...(url && { url }),
		priceCurrency: context.currency,
		price,
		priceValidUntil: context.priceValidUntil,
		itemCondition: `${SCHEMA}/NewCondition`,
		availability: getVariantAvailability(product, item),
		seller: { '@id': context.sellerId },
		shippingDetails: { '@id': STRUCTURED_DATA_ID.SHIPPING_POLICY },
		hasMerchantReturnPolicy: { '@id': STRUCTURED_DATA_ID.RETURN_POLICY },
	};
};

export const buildShippingDetails = ({ codeISO, currencyISO }) => ({
	'@context': SCHEMA,
	'@type': 'OfferShippingDetails',
	'@id': STRUCTURED_DATA_ID.SHIPPING_POLICY,
	shippingRate: {
		'@type': 'MonetaryAmount',
		value: SHIPPING_POLICY.rateValue,
		currency: currencyISO,
	},
	shippingDestination: {
		'@type': 'DefinedRegion',
		addressCountry: codeISO,
	},
	deliveryTime: {
		'@type': 'ShippingDeliveryTime',
		handlingTime: {
			'@type': 'QuantitativeValue',
			minValue: SHIPPING_POLICY.handlingTimeDays.min,
			maxValue: SHIPPING_POLICY.handlingTimeDays.max,
			unitCode: 'DAY',
		},
		transitTime: {
			'@type': 'QuantitativeValue',
			minValue: SHIPPING_POLICY.transitTimeDays.min,
			maxValue: SHIPPING_POLICY.transitTimeDays.max,
			unitCode: 'DAY',
		},
	},
});

export const buildReturnPolicy = ({ codeISO }) => ({
	'@context': SCHEMA,
	'@type': 'MerchantReturnPolicy',
	'@id': STRUCTURED_DATA_ID.RETURN_POLICY,
	applicableCountry: codeISO,
	returnPolicyCountry: codeISO,
	returnPolicyCategory: RETURN_POLICY.category,
	merchantReturnDays: RETURN_POLICY.days,
	returnMethod: RETURN_POLICY.method,
	returnFees: RETURN_POLICY.fees,
});
